import { filter, isEmpty } from "lodash";
import React, { Key, useCallback, useEffect, useState } from "react";

import {
  ACDateButtons,
  ACDateSelector,
  ActiveButton,
  DateType,
  Value,
  Values,
} from "ac-components/ACDateRangeFilter";
import { ACFilterDropdownProps } from "ac-components/ac-filter-bar/ACFilterBar";

import "./index.less";

type Props = ACFilterDropdownProps;

export default function ACDateRangeColumnFilter({
  selectedKeys,
  confirm,
  setSelectedKeys,
  visible,
}: Props) {
  const [value, setValue] = useState<Values>(selectedKeys);
  const [activeDate, setActiveDate] = useState<ActiveButton>("start");
  const [changed, setChanged] = useState(false);
  useEffect(() => {
    if (visible) {
      setActiveDate("start");
      setValue(selectedKeys);
      setChanged(false);
    }
  }, [visible, selectedKeys]);

  useEffect(() => {
    if (isEmpty(selectedKeys)) {
      setValue([undefined, "now"]);
    } else setValue(selectedKeys);
  }, [selectedKeys]);

  const _onChange = useCallback(
    (dateValue?: Value, type?: DateType) => {
      setChanged(true);
      let _value: Values;
      if (!value) {
        _value = undefined;
      }
      if (activeDate === "start") {
        _value = [dateValue === " d" ? "" : dateValue, value?.[1]];
      } else {
        _value = [value?.[0], dateValue === " d" ? "" : dateValue];
      }
      setValue(_value as React.Key[]);
      if (activeDate === "start" && !_value[1] && type === "absolute") {
        // HACK ENG-45193 to reflect the date selected in calendar else start date is seen selected in end date if we set state directly
        setTimeout(() => setActiveDate("end"));
      }
    },
    [activeDate, value],
  );

  const onApply = useCallback(() => {
    const filteredValues = filter(value, (v) => v !== undefined) as Key[];
    setSelectedKeys(filteredValues);
    confirm();
  }, [value, setSelectedKeys, confirm]);
  const onClear = useCallback(() => {
    setSelectedKeys([]);
    confirm();
  }, [setSelectedKeys, confirm]);

  const handleSwap = () => {
    if (value && value.length === 2) {
      let [start, end] = value;

      // the below code handles the swapping of start and end values, the start and end values can be like this - [3d, 5d+]
      // here 3d means 3 days ago and 5d+ means 5 days from now, so to handle swapping of these to [5d, 3d+] the below code is required
      if (typeof start === "string" && start !== "now") {
        start = start.includes("+") ? start.replace("+", "") : `${start}+`;
      }
      if (typeof end === "string" && end !== "now") {
        end = end.includes("+") ? end.replace("+", "") : `${end}+`;
      }
      const newValues = [end, start] as Values;
      setValue(newValues);
      setChanged(true);
    }
  };

  return (
    <div className="column-filter ac-date-range-column-filter">
      <ACDateButtons
        onChange={setActiveDate}
        activeDate={activeDate}
        value={value}
        onSwap={handleSwap}
      />
      <div className="m-b-xs border-bottom-light" />
      <ACDateSelector
        activeDate={activeDate}
        onChange={_onChange}
        value={value}
        onApply={onApply}
        onClear={onClear}
        valueChanged={changed}
      />
    </div>
  );
}
